import { useState, useEffect } from 'react';
import {
    Users, Plus, Edit2, Trash2, Shield, Eye, EyeOff,
    X, Search, Mail, User as UserIcon, Check, AlertTriangle
} from 'lucide-react';
import API_URL from '../config/api';
import './UserManager.css';

const UserManager = () => {
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        role_id: '',
        is_active: true
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        await Promise.all([fetchUsers(), fetchRoles()]);
        setLoading(false);
    };

    const fetchUsers = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/users`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setUsers(data);
            }
        } catch (err) {
            console.error('Error fetching users:', err);
        }
    };

    const fetchRoles = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/roles`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setRoles(data);
            }
        } catch (err) {
            console.error('Error fetching roles:', err);
        }
    };

    const handleEdit = (user) => {
        setEditingUser(user);
        setFormData({
            name: user.name,
            email: user.email,
            password: '',
            role_id: user.role_id,
            is_active: user.is_active == 1
        });
        setShowModal(true);
        setError('');
    };

    const handleNew = () => {
        setEditingUser(null);
        setFormData({
            name: '',
            email: '',
            password: '',
            role_id: roles[0]?.id || '',
            is_active: true
        });
        setShowModal(true);
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!formData.name || !formData.email || !formData.role_id) {
            setError('Nombre, email y rol son requeridos');
            return;
        }

        if (!editingUser && !formData.password) {
            setError('La contraseña es requerida para nuevos usuarios');
            return;
        }

        try {
            setSubmitting(true);
            const token = localStorage.getItem('token');

            const url = editingUser
                ? `${API_URL}/users/${editingUser.id}`
                : `${API_URL}/users`;

            const method = editingUser ? 'PUT' : 'POST';

            const body = { ...formData };
            if (editingUser && !formData.password) {
                delete body.password;
            }

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(body)
            });

            const data = await response.json();

            if (response.ok) {
                setShowModal(false);
                fetchUsers();
            } else {
                setError(data.message || 'Error al guardar');
            }
        } catch (err) {
            setError('Error de conexión');
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleActive = async (userId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/users/${userId}/toggle-active`, {
                method: 'PATCH',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                fetchUsers();
            }
        } catch (err) {
            console.error('Error toggling status:', err);
        }
    };

    const handleDelete = async (userId) => {
        if (!confirm('¿Está seguro de eliminar este usuario? Esta acción no se puede deshacer.')) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/users/${userId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                fetchUsers();
            }
        } catch (err) {
            console.error('Error deleting user:', err);
        }
    };

    const filteredUsers = users.filter(user =>
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.role_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getRoleBadgeColor = (roleName) => {
        const colors = {
            'admin': 'badge-admin',
            'superadmin': 'badge-superadmin',
            'medico': 'badge-doctor',
            'recepcion': 'badge-reception',
            'laboratorio': 'badge-lab',
            'lab tech': 'badge-lab'
        };
        return colors[roleName?.toLowerCase()] || 'badge-default';
    };

    if (loading) {
        return (
            <div className="user-manager-loading">
                <div className="spinner"></div>
                <p>Cargando usuarios...</p>
            </div>
        );
    }

    return (
        <div className="user-manager">
            <div className="manager-header">
                <div className="manager-title-wrapper">
                    <div className="manager-title">
                        <Users size={28} />
                        <h2>Gestión de Personal</h2>
                    </div>
                    <p>{users.length} usuarios registrados</p>
                </div>
                <button className="btn-add" onClick={handleNew}>
                    <Plus size={20} />
                    Nuevo Usuario
                </button>
            </div>

            <div className="search-bar">
                <input
                    type="text"
                    placeholder="Buscar por nombre, email o rol..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            <div className="users-grid">
                {filteredUsers.map(user => (
                    <div key={user.id} className={`user-card ${user.is_active == 0 ? 'inactive' : ''}`}>
                        <div className="user-avatar">
                            <UserIcon size={24} />
                        </div>
                        <div className="user-info">
                            <h3>{user.name}</h3>
                            <p className="user-email">
                                <Mail size={14} />
                                {user.email}
                            </p>
                            <span className={`role-badge ${getRoleBadgeColor(user.role_name)}`}>
                                <Shield size={12} />
                                {user.role_name}
                            </span>
                        </div>
                        <div className="user-status">
                            {user.is_active == 1 ? (
                                <span className="status-active"><Check size={14} /> Activo</span>
                            ) : (
                                <span className="status-inactive"><AlertTriangle size={14} /> Inactivo</span>
                            )}
                            <div className="user-actions">
                                <button
                                    className="btn-icon btn-toggle"
                                    onClick={() => handleToggleActive(user.id)}
                                    title={user.is_active == 1 ? 'Desactivar' : 'Activar'}
                                >
                                    {user.is_active == 1 ? <EyeOff size={20} /> : <Eye size={20} />}
                                    <span>{user.is_active == 1 ? 'Activado' : 'Desactivado'}</span>
                                </button>
                                <button
                                    className="btn-icon btn-edit"
                                    onClick={() => handleEdit(user)}
                                    title="Editar"
                                >
                                    <Edit2 size={20} />
                                    <span>Editar</span>
                                </button>
                                <button
                                    className="btn-icon btn-delete"
                                    onClick={() => handleDelete(user.id)}
                                    title="Eliminar"
                                >
                                    <Trash2 size={20} />
                                    <span>Eliminar</span>
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {filteredUsers.length === 0 && (
                <div className="empty-state">
                    <Users size={48} />
                    <p>No se encontraron usuarios</p>
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}</h3>
                            <button className="close-btn" onClick={() => setShowModal(false)}>
                                <X size={24} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            {error && (
                                <div className="error-message">
                                    <AlertTriangle size={16} />
                                    {error}
                                </div>
                            )}

                            <div className="form-group">
                                <label>Nombre completo</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="Nombre del usuario"
                                />
                            </div>

                            <div className="form-group">
                                <label>Correo electrónico</label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                                    placeholder="email@ejemplo.com"
                                />
                            </div>

                            <div className="form-group">
                                <label>
                                    Contraseña {editingUser && <span className="hint">(dejar vacío para no cambiar)</span>}
                                </label>
                                <input
                                    type="password"
                                    value={formData.password}
                                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                                    placeholder={editingUser ? '••••••••' : 'Contraseña segura'}
                                />
                            </div>

                            <div className="form-group">
                                <label>Rol</label>
                                <select
                                    value={formData.role_id}
                                    onChange={(e) => setFormData(prev => ({ ...prev, role_id: e.target.value }))}
                                >
                                    <option value="">Seleccionar rol...</option>
                                    {roles
                                        .filter(role => !['enfermera', 'cobranza', 'paciente'].includes(role.name?.toLowerCase()))
                                        .map(role => (
                                        <option key={role.id} value={role.id}>{role.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group checkbox-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={formData.is_active}
                                        onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                                    />
                                    Usuario activo
                                </label>
                            </div>

                            <div className="form-actions">
                                <button
                                    type="button"
                                    className="btn-cancel"
                                    onClick={() => setShowModal(false)}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="btn-submit"
                                    disabled={submitting}
                                >
                                    {submitting ? 'Guardando...' : (editingUser ? 'Actualizar' : 'Crear Usuario')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManager;

